import type { HouseholdChannel } from "./types";

/** Fila del padrón lista para enviar al API (importación masiva). */
export interface ParsedHouseholdRow {
  name: string;
  phone: string;
  lat: number;
  lng: number;
  leadMinutes: number;
  channels: HouseholdChannel[];
}

export interface CsvParseResult {
  rows: ParsedHouseholdRow[];
  errors: string[];
}

const CHANNEL_ALIASES: Record<string, HouseholdChannel> = {
  push: "push",
  app: "push",
  notificacion: "push",
  notificación: "push",
  whatsapp: "whatsapp",
  llamada: "call",
  call: "call",
};

function splitLine(line: string): string[] {
  const cells: string[] = [];
  let current = "";
  let quoted = false;
  for (let i = 0; i < line.length; i += 1) {
    const char = line[i];
    if (char === '"') {
      if (quoted && line[i + 1] === '"') {
        current += '"';
        i += 1;
      } else {
        quoted = !quoted;
      }
    } else if ((char === "," || char === ";") && !quoted) {
      cells.push(current.trim());
      current = "";
    } else {
      current += char;
    }
  }
  cells.push(current.trim());
  return cells;
}

function headerIndex(header: string[], ...names: string[]): number {
  return header.findIndex((value) => names.includes(value.trim().toLowerCase()));
}

/**
 * Parsea un CSV de hogares con cabecera flexible:
 * `nombre|name`, `telefono|phone`, `lat`, `lng|lon|longitud`, `anticipacion|lead_minutes`, `canales|channels`.
 * Canal por defecto: push. Líneas vacías se ignoran.
 */
export function parseHouseholdsCsv(text: string): CsvParseResult {
  const errors: string[] = [];
  const rows: ParsedHouseholdRow[] = [];
  const lines = text.split(/\r?\n/).filter((line) => line.trim() !== "");
  if (lines.length === 0) return { rows, errors: ["El archivo está vacío."] };

  const header = splitLine(lines[0] ?? "");
  const nameIndex = headerIndex(header, "nombre", "name", "hogar");
  const phoneIndex = headerIndex(header, "telefono", "teléfono", "phone", "celular");
  const latIndex = headerIndex(header, "lat", "latitud");
  const lngIndex = headerIndex(header, "lng", "lon", "longitud");
  const leadIndex = headerIndex(header, "anticipacion", "anticipación", "lead_minutes", "lead");
  const channelsIndex = headerIndex(header, "canales", "channels");
  if (nameIndex < 0 || latIndex < 0 || lngIndex < 0) {
    return {
      rows,
      errors: ["La cabecera debe incluir al menos: nombre, lat, lng (opcional: telefono, anticipacion, canales)."],
    };
  }

  lines.slice(1).forEach((line, offset) => {
    const lineNumber = offset + 2;
    const cells = splitLine(line);
    const name = (cells[nameIndex] ?? "").trim();
    const lat = Number.parseFloat((cells[latIndex] ?? "").replace(",", "."));
    const lng = Number.parseFloat((cells[lngIndex] ?? "").replace(",", "."));
    if (name === "" || !Number.isFinite(lat) || !Number.isFinite(lng)) {
      errors.push(`Línea ${lineNumber}: nombre, lat o lng inválidos.`);
      return;
    }
    const lead = leadIndex < 0 ? 10 : Number.parseInt(cells[leadIndex] ?? "", 10);
    const leadMinutes = Number.isFinite(lead) && lead >= 5 && lead <= 120 ? lead : 10;
    const rawChannels = channelsIndex < 0 ? "" : (cells[channelsIndex] ?? "");
    const channels = rawChannels
      .split(/[|;/]/)
      .map((value) => CHANNEL_ALIASES[value.trim().toLowerCase()])
      .filter((value): value is HouseholdChannel => value !== undefined);
    rows.push({
      name,
      phone: phoneIndex < 0 ? "" : (cells[phoneIndex] ?? "").trim(),
      lat,
      lng,
      leadMinutes,
      channels: channels.length > 0 ? channels : ["push"],
    });
  });

  return { rows, errors };
}

function csvCell(value: string | number): string {
  const text = String(value);
  return /[",\n]/.test(text) ? `"${text.replace(/"/g, '""')}"` : text;
}

export function toCsv(headers: string[], rows: readonly (string | number)[][]): string {
  return [headers, ...rows].map((row) => row.map(csvCell).join(",")).join("\n");
}

export function downloadCsv(filename: string, content: string): void {
  const blob = new Blob([`\uFEFF${content}`], { type: "text/csv;charset=utf-8" });
  const url = URL.createObjectURL(blob);
  const link = document.createElement("a");
  link.href = url;
  link.download = filename;
  document.body.append(link);
  link.click();
  link.remove();
  URL.revokeObjectURL(url);
}
